// Iter61 — Wheel ITM Scanner Visibility Adapter / Panel.
//
// Read-only passthrough view over the PROTECTED flagship Wheel ITM
// Decision Engine. The panel ONLY calls the existing endpoint
//
//     GET /api/forge/candidates?symbol=<SYM>[&timeframe=<tf>][&expiration=<exp>]
//
// per manual click. Wheel scoring, checklists, ROI gate, WES, WES-GEX,
// and decision logic are NEVER recomputed or duplicated here — every
// field rendered comes verbatim from the existing engine response.
//
// Hard isolation:
//   • No auto-fire on mount.
//   • No multi-module fanout (BPCS / BCCS / Iron Condor / CFM
//     scanners are never referenced).
//   • No broker, journal, or DB endpoints touched.
//   • No execution. No rank vs. other strategies.

import React, { useState } from "react";
import axios from "axios";
import {
  AlertTriangle, ChevronDown, ChevronUp, Play, Shield,
  ShieldAlert, CheckCircle2, Eraser, Eye,
} from "lucide-react";
import { API } from "@/App";
import ScannerResultActions from "@/components/ScannerResultActions";
// Iter108 — shared visual primitive: company logo on Wheel ITM cards.
import { CompanyLogo } from "@/components/CompanyLogo";

// Single allowed endpoint — hardcoded, never derived from user input.
const WHEEL_ENDPOINT = "/forge/candidates";

const DECISION_BADGE = {
  TRADE:    "border-[#00FF88]/40 bg-[#00FF88]/10 text-[#00FF88]",
  PURE:     "border-[#00FF88]/40 bg-[#00FF88]/10 text-[#00FF88]",
  LADDER:   "border-[#00FF88]/40 bg-[#00FF88]/10 text-[#00FF88]",
  NO_TRADE: "border-[#5F5F66]/40 bg-[#121216] text-[#8A8A93]",
  "NO TRADE": "border-[#5F5F66]/40 bg-[#121216] text-[#8A8A93]",
  ERROR:    "border-[#FF3366]/40 bg-[#FF3366]/10 text-[#FF3366]",
};

export default function WheelITMScannerPanel() {
  const [open, setOpen] = useState(false);
  const [symbol, setSymbol] = useState("SPY");
  const [expiration, setExpiration] = useState("");
  const [timeframe, setTimeframe] = useState("1h");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [result, setResult] = useState(null);
  const [lastRequest, setLastRequest] = useState(null);

  async function runScanner() {
    setErr(null);
    const sym = (symbol || "").trim().toUpperCase();
    if (!sym) {
      setErr("Symbol required.");
      return;
    }
    setLoading(true);
    try {
      const params = { symbol: sym };
      if (timeframe) params.timeframe = timeframe;
      if (expiration) params.expiration = expiration;
      const r = await axios.get(`${API}${WHEEL_ENDPOINT}`, { params });
      setResult(r.data);
      setLastRequest(params);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "Network error");
    } finally {
      setLoading(false);
    }
  }

  function resetForm() {
    setSymbol("SPY");
    setExpiration("");
    setTimeframe("1h");
    setErr(null);
  }

  function clearResult() {
    setResult(null);
    setErr(null);
  }

  return (
    <div
      data-testid="wheel-itm-scanner-panel"
      className="mb-5 rounded-sm border border-[#26262B] bg-[#0A0A0E]"
    >
      <button
        data-testid="wheel-itm-scanner-toggle"
        onClick={() => setOpen(v => !v)}
        type="button"
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[#121216]"
      >
        <Shield size={16} className="text-[#00FF88]" />
        <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#E0E0E0]">
          Wheel ITM / CRSP — Candidates Visibility
        </span>
        <span className="text-[9px] font-mono uppercase tracking-[0.12em] text-[#5F5F66]">
          · Protected flagship · Existing engine passthrough · Manual-run-per-click
        </span>
        <span className="ml-auto text-[#5F5F66]">
          {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </span>
      </button>

      {open && (
        <div className="border-t border-[#26262B]">
          {/* ── Protection banner ──────────────────────────────── */}
          <div
            data-testid="wheel-itm-scanner-banner"
            className="px-4 py-2 border-b border-[#26262B] bg-[#00FF88]/5 text-[#00FF88] text-[10px] font-mono flex items-center gap-3 flex-wrap"
          >
            <ShieldAlert size={12} />
            <span data-testid="wheel-itm-scanner-banner-protected">
              Wheel ITM / CRSP — protected flagship.
            </span>
            <span className="text-[#00FF88]/50">·</span>
            <span data-testid="wheel-itm-scanner-banner-passthrough">
              Existing engine passthrough.
            </span>
            <span className="text-[#00FF88]/50">·</span>
            <span data-testid="wheel-itm-scanner-banner-roi-gate">
              ROI gate preserved.
            </span>
            <span className="text-[#00FF88]/50">·</span>
            <span data-testid="wheel-itm-scanner-banner-wes-unchanged">
              WES / WES-GEX logic unchanged.
            </span>
            <span className="text-[#00FF88]/50">·</span>
            <span data-testid="wheel-itm-scanner-banner-no-execution">
              No execution enabled.
            </span>
          </div>

          {/* ── Form + run controls ───────────────────────────── */}
          <div className="px-4 py-3 border-b border-[#26262B]">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-[#8A8A93]">
                Request
              </span>
              <span className="text-[9px] font-mono text-[#5F5F66] truncate"
                    title={WHEEL_ENDPOINT}>
                {WHEEL_ENDPOINT}
              </span>
              <div className="ml-auto flex items-center gap-2">
                <button
                  type="button"
                  data-testid="wheel-itm-scanner-reset"
                  onClick={resetForm}
                  className="px-2 py-1 rounded-sm border border-[#26262B] text-[9px] font-mono uppercase tracking-[0.12em] text-[#8A8A93] hover:text-[#E0E0E0] hover:border-[#3F3F47] inline-flex items-center gap-1"
                >
                  <Eraser size={10} /> Reset
                </button>
                <button
                  type="button"
                  data-testid="wheel-itm-scanner-run"
                  onClick={runScanner}
                  disabled={loading}
                  className="px-3 py-1 rounded-sm border border-[#00FF88]/40 bg-[#00FF88]/10 text-[10px] font-mono uppercase tracking-[0.14em] text-[#00FF88] hover:bg-[#00FF88]/20 disabled:opacity-40 inline-flex items-center gap-1"
                >
                  <Play size={10} /> {loading ? "Loading…" : "Run Wheel ITM Lookup"}
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <label className="flex flex-col gap-1">
                <span className="text-[9px] font-mono uppercase tracking-[0.14em] text-[#5F5F66]">
                  Symbol
                </span>
                <input
                  data-testid="wheel-itm-scanner-symbol"
                  type="text"
                  value={symbol}
                  onChange={(e) => setSymbol(e.target.value.toUpperCase())}
                  spellCheck={false}
                  className="bg-[#08080A] border border-[#26262B] rounded-sm px-2 py-1.5 text-[11px] font-mono text-[#E0E0E0] focus:outline-none focus:border-[#00FF88]/50 uppercase"
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-[9px] font-mono uppercase tracking-[0.14em] text-[#5F5F66]">
                  Timeframe (optional)
                </span>
                <input
                  data-testid="wheel-itm-scanner-timeframe"
                  type="text"
                  value={timeframe}
                  onChange={(e) => setTimeframe(e.target.value)}
                  spellCheck={false}
                  className="bg-[#08080A] border border-[#26262B] rounded-sm px-2 py-1.5 text-[11px] font-mono text-[#E0E0E0] focus:outline-none focus:border-[#00FF88]/50"
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-[9px] font-mono uppercase tracking-[0.14em] text-[#5F5F66]">
                  Expiration (optional, YYYY-MM-DD)
                </span>
                <input
                  data-testid="wheel-itm-scanner-expiration"
                  type="text"
                  value={expiration}
                  onChange={(e) => setExpiration(e.target.value)}
                  spellCheck={false}
                  className="bg-[#08080A] border border-[#26262B] rounded-sm px-2 py-1.5 text-[11px] font-mono text-[#E0E0E0] focus:outline-none focus:border-[#00FF88]/50"
                />
              </label>
            </div>
          </div>

          {/* ── State views ───────────────────────────────────── */}
          {loading && (
            <div
              data-testid="wheel-itm-scanner-loading"
              className="px-4 py-3 text-[10px] font-mono text-[#5F5F66]"
            >
              Calling protected Wheel ITM engine…
            </div>
          )}

          {err && !loading && (
            <div
              data-testid="wheel-itm-scanner-error"
              className="px-4 py-3 flex items-start gap-2 text-[10px] font-mono text-[#FF3366]"
            >
              <AlertTriangle size={11} className="shrink-0 mt-0.5" />
              <span>{err}</span>
            </div>
          )}

          {!loading && !err && !result && (
            <div
              data-testid="wheel-itm-scanner-empty"
              className="px-4 py-3 text-[10px] font-mono text-[#5F5F66]"
            >
              No lookup run yet. Enter a symbol above and click
              Run Wheel ITM Lookup.
            </div>
          )}

          {result && !loading && !err && (
            <WheelResult result={result} onClear={clearResult} lastRequest={lastRequest} />
          )}
        </div>
      )}
    </div>
  );
}

function Field({ label, value, testid, mono = true }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[9px] font-mono uppercase tracking-[0.14em] text-[#5F5F66]">
        {label}
      </span>
      <span
        data-testid={testid}
        className={`text-[11px] ${mono ? "font-mono" : ""} text-[#E0E0E0] break-words`}
      >
        {value === null || value === undefined || value === "" ? "—" : String(value)}
      </span>
    </div>
  );
}

function WheelResult({ result, onClear, lastRequest }) {
  const decision = result.decision || {};
  const strategy = decision.strategy || decision.decision || "NO_TRADE";
  const badgeClass = DECISION_BADGE[strategy] || DECISION_BADGE.NO_TRADE;
  return (
    <div
      data-testid="wheel-itm-scanner-result"
      className="px-4 py-3 space-y-3"
    >
      <div className="flex items-center gap-2 flex-wrap text-[10px] font-mono">
        <CheckCircle2 size={11} className="text-[#00FF88]" />
        {/* Iter108 — company logo (FMP CDN, monogram fallback). */}
        <CompanyLogo
          symbol={result.symbol || ""}
          size={20}
          testid="wheel-itm-scanner-result-logo"
        />
        <span
          data-testid="wheel-itm-scanner-result-symbol"
          className="text-[#E0E0E0] uppercase tracking-[0.14em] font-bold"
        >
          {result.symbol || "—"}
        </span>
        <span
          data-testid="wheel-itm-scanner-result-strategy"
          className={`px-2 py-0.5 rounded-sm border text-[9px] uppercase tracking-[0.14em] ${badgeClass}`}
        >
          {strategy}
        </span>
        <span className="text-[#5F5F66] uppercase tracking-[0.12em]">
          existing engine output (verbatim)
        </span>
        <button
          type="button"
          data-testid="wheel-itm-scanner-result-clear"
          onClick={onClear}
          className="ml-auto text-[#5F5F66] hover:text-[#E0E0E0] uppercase tracking-[0.12em]"
        >
          Clear
        </button>
        <ScannerResultActions
          result={result}
          request={lastRequest}
          endpoint={WHEEL_ENDPOINT}
          sourcePanel="WheelITMScannerPanel"
          moduleId="WHEEL_ITM_CRSP"
          testidPrefix="wheel-itm-scanner-result"
        />
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Field label="Spot" value={result.spot}
               testid="wheel-itm-scanner-result-spot" />
        <Field label="Expiration" value={result.expiration}
               testid="wheel-itm-scanner-result-expiration" />
        <Field label="DTE" value={result.dte}
               testid="wheel-itm-scanner-result-dte" />
        <Field label="Min income %" value={result.min_income_pct}
               testid="wheel-itm-scanner-result-min-income" />
        <Field label="WES-P" value={result.wes_p}
               testid="wheel-itm-scanner-result-wes-p" />
        <Field label="WES-C" value={result.wes_c}
               testid="wheel-itm-scanner-result-wes-c" />
        <Field label="Bias" value={result.bias} mono={false}
               testid="wheel-itm-scanner-result-bias" />
        <Field label="Expiration note" value={result.expiration_note}
               testid="wheel-itm-scanner-result-exp-note" />
      </div>

      {decision && (
        <div className="rounded-sm border border-[#26262B] bg-[#08080A] p-2">
          <div className="text-[9px] font-mono uppercase tracking-[0.14em] text-[#5F5F66] mb-1">
            Decision (engine verbatim)
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-2">
            <Field label="Strike" value={decision.strike}
                   testid="wheel-itm-scanner-result-decision-strike" />
            <Field label="ROI %" value={decision.roi_pct}
                   testid="wheel-itm-scanner-result-decision-roi" />
            <Field label="Premium bid" value={decision.premium_bid}
                   testid="wheel-itm-scanner-result-decision-premium-bid" />
            <Field label="Premium mid" value={decision.premium_mid}
                   testid="wheel-itm-scanner-result-decision-premium-mid" />
          </div>
          {decision.reason && (
            <div
              data-testid="wheel-itm-scanner-result-decision-reason"
              className="text-[10px] font-mono text-[#E0E0E0] leading-relaxed"
            >
              {decision.reason}
            </div>
          )}
          {decision.execution_plan && (
            <div
              data-testid="wheel-itm-scanner-result-decision-execution-plan"
              className="mt-1 text-[10px] font-mono text-[#8A8A93] leading-relaxed flex items-start gap-1"
            >
              <Eye size={10} className="mt-0.5 shrink-0" />
              {decision.execution_plan}
            </div>
          )}
        </div>
      )}

      <details data-testid="wheel-itm-scanner-result-pure-wheel">
        <summary className="cursor-pointer text-[10px] font-mono uppercase tracking-[0.14em] text-[#8A8A93] hover:text-[#E0E0E0]">
          Pure Wheel candidate (engine verbatim)
        </summary>
        <pre className="mt-1 text-[10px] font-mono text-[#E0E0E0] whitespace-pre-wrap break-words bg-[#0A0A0E] p-2 rounded-sm border border-[#26262B] max-h-64 overflow-auto">
          {JSON.stringify(result.pure_wheel ?? null, null, 2)}
        </pre>
      </details>

      <details data-testid="wheel-itm-scanner-result-ladder">
        <summary className="cursor-pointer text-[10px] font-mono uppercase tracking-[0.14em] text-[#8A8A93] hover:text-[#E0E0E0]">
          Ladder candidate (engine verbatim)
        </summary>
        <pre className="mt-1 text-[10px] font-mono text-[#E0E0E0] whitespace-pre-wrap break-words bg-[#0A0A0E] p-2 rounded-sm border border-[#26262B] max-h-64 overflow-auto">
          {JSON.stringify(result.ladder ?? null, null, 2)}
        </pre>
      </details>

      <details data-testid="wheel-itm-scanner-result-chain">
        <summary className="cursor-pointer text-[10px] font-mono uppercase tracking-[0.14em] text-[#8A8A93] hover:text-[#E0E0E0]">
          Chain rows (engine verbatim)
        </summary>
        <pre className="mt-1 text-[10px] font-mono text-[#E0E0E0] whitespace-pre-wrap break-words bg-[#0A0A0E] p-2 rounded-sm border border-[#26262B] max-h-64 overflow-auto">
          {JSON.stringify(result.chain ?? [], null, 2)}
        </pre>
      </details>
    </div>
  );
}
